import { Component } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { Grid, Typography, CardContent, Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';

import { composeGCSUrl } from '../../shared_client_utils/googleCloudStorageUtils';
import IdleTimer from '../../shared_components/IdleTimer';
import BookingFlow from '../../shared_components/booking/flows/Creation';
import LocationStep from '../../shared_components/booking/LocationStep';
import ServiceStep from '../../shared_components/booking/ServiceStep';
import StaffStep from '../../shared_components/booking/StaffStep';
import DatetimeStep from '../../shared_components/booking/DatetimeStep';
import ClientStep from '../../shared_components/booking/ClientStep';
import CompleteStep from '../../shared_components/booking/CompleteStep';
import CircularProgressWithBackdrop from '../../shared_components/CircularProgressWithBackdrop';
import styles from '../../shared_components/booking/styles.js';
import { loadBusinessBySlugForBooking } from '../../shared_slices/businessesSlice';
import { loadLocationsForBooking } from '../../shared_slices/locationsSlice';
import { loadClientForBooking } from '../../shared_slices/clientSlice';
import { createClientForBooking, verifyClientCreditCard, createClientCreditCard } from '../../shared_slices/paymentSlice';
import {
  loadCategoriesWithServicesForBooking,
} from '../../shared_slices/serviceCategoriesSlice';
import { loadStaffForBooking } from '../../shared_slices/staffSlice';
import { BookingApi, CustomersApi, MailchimpApi } from '../../client_http_api';
import {
  getClientDataFromBrowser,
  keepClientDataInBrowser,
  clearClientDataFromBrowser,
} from '../../shared_client_utils/bookingUtils';
import { getCurrentClientTime } from '../../shared_client_utils/dateUtils';
import withFlashMessage from '../../shared_hocs/withFlashMessage'
import axios from 'axios';

const prepareCategories =
  object => Object.entries(object).map(([, element]) => element);

const idleMinutes = 30;

const stepComponents = {
  1: LocationStep,
  2: ServiceStep,
  3: StaffStep,
  4: DatetimeStep,
  5: ClientStep,
  6: CompleteStep,
};

const initializeClient = () => ({
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
  landlineNumber: ''
});

const prepareQuerySlugs = ({ location, staff, category, service }) => ({
  locationSlug: location || '',
  staffSlug: staff || '',
  serviceCategorySlug: category || '',
  serviceSlug: service || '',
});

const areThereAnySlugs = (slugs) => {
  const { locationSlug, staffSlug, serviceCategorySlug, serviceSlug } = slugs;
  return locationSlug || staffSlug || serviceCategorySlug || serviceSlug;
};

const prepareData = (appointment, client, businessId, stepsResultData, overrideUnmatchedField, createNewClientIfFirstNameNotMatches, tempUnavailableId) => {
  const { notes, serviceBookingAnswer } = appointment;
  const { bookingQuestion, ...restStepsResultData } = stepsResultData;

  const serviceBookingQuestion = serviceBookingAnswer ? bookingQuestion : '';

  return {
    client,
    appointment: {
      ...restStepsResultData,
      notes,
      businessId,
      serviceBookingAnswer,
      serviceBookingQuestion,
    },
    siteUrl: window.location.origin,
    currentClientTime: getCurrentClientTime(),
    overrideUnmatchedField,
    createNewClientIfFirstNameNotMatches,
    tempUnavailableId
  };
};

class BookPage extends Component {
  constructor(props) {
    super(props);

    const guessTZ = moment.tz.guess();
    moment.tz.setDefault(guessTZ);

    this.state = {
      isLoading: true,
      slug: '',
      currentStep: null,
      stepsData: {},
      appointment: {
        notes: '',
        serviceBookingAnswer: '',
      },
      client: initializeClient(),
      bookingOptions: {
        rememberMe: false,
        agreePrivacyPolicy: false,
        agreeCancellationPolicy: false,
      },
      isBookingInitialized: false,
      isSessionExpired: false,
      areThereBookableServices: true,
      tempUnavailableId: null,
      unloadListener: null,
    };

    this.idleTimer = null;
    this.handleExpiredSession = this.handleExpiredSession.bind(this);
    this.handlePrevStep = this.handlePrevStep.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handleCompliteBooking = this.handleCompliteBooking.bind(this);
    this.handleChangeAppointment = this.handleChangeAppointment.bind(this);
    this.handleChangeClient = this.handleChangeClient.bind(this);
    this.handleChangeBookingOptions = this.handleChangeBookingOptions.bind(this);
    this.handleSubscribeClient = this.handleSubscribeClient.bind(this);
    this.setTempUnavailableId = this.setTempUnavailableId.bind(this);
  }

  async componentDidMount() {
    const { slug, ...query } = this.props.router.query;
    this.getClientIp()
    
    // TODO: load business on server side
    const {
      payload: business,
    } = await this.props.loadBusinessBySlugForBooking(slug);
    if (!business) {
      this.props.handleDisplayFlashMessage('Something went wrong', 'error');
      this.handleStopLoading();
      return;
    }

    moment.tz.setDefault(business.timezone);

    const urlQuerySlugs = prepareQuerySlugs(query);
    let urlQueryIds = {};
    if (areThereAnySlugs(urlQuerySlugs)) {
      const preparedSlugs = JSON.stringify(urlQuerySlugs);
      urlQueryIds = await BookingApi.fetchIdsBySlugs(preparedSlugs, business.id);
    }

    this.bookingFlow = new BookingFlow({
      businessId: business.id,
      bookingSettings: business.bookingSettings,
    });
    let stepsData = this.bookingFlow.initializeStepsData();
    stepsData = await this.bookingFlow.updateAndHideStepsByUrlQueryIds(
      stepsData,
      urlQueryIds,
      this.props,
    )
    stepsData = this.bookingFlow.hideStaffStepByBookingSetting(stepsData);
    stepsData = await this.bookingFlow.updateSupposedFirstStepByExternalRecords(
      stepsData,
      this.props,
    );

    let currentStep = this.bookingFlow.getFirstStep(stepsData);
    if (!currentStep) {
      this.setState({
        areThereBookableServices: false,
      }, this.handleStopLoading);
      return;
    }

    if (currentStep.isLocation) { // Because the location step has selected option by default
      stepsData = await this.bookingFlow.prepareNextStep(
        currentStep,
        stepsData,
        this.props,
      );
    }

    const clientData = getClientDataFromBrowser();

    this.setState(({ client, bookingOptions }) => ({
      slug,
      stepsData,
      currentStep,
      isBookingInitialized: true,
      client: clientData
        ? {...clientData,
          ...(client.clientIpAddress ? {clientIpAddress: client.clientIpAddress} : {}),
          ...(client.lat ? { lat: client.lat } : {}),
          ...(client.lng ? { lng: client.lng } : {}),
          ...(client.clientTimezone ? { clientTimezone: client.clientTimezone } : {}),
          ...(client.browserVersion ? { browserVersion: client.browserVersion } : {}),
          ...(client.countryCode ? { countryCode: client.countryCode } : {}),
          ...(client.country ? { country: client.country } : {}),
          ...(client.city ? { city: client.city } : {})
        }
        : client,
      bookingOptions: {
        ...bookingOptions,
        rememberMe: clientData ? true : false,
      },
    }), this.handleStopLoading);
  }

  componentWillUnmount() {
    moment.tz.setDefault();
  }

  async componentDidUpdate(prevProps, prevState) {
    const { business, serviceCategories } = this.props;
    if (this.state.currentStep && prevState.currentStep?.id !== this.state.currentStep.id) {
      const pageTitle = `Hivemanager - ${this.state.currentStep.title}`
      document.title = pageTitle
      const search = `${window.location.search ? `${window.location.search}&` : '?'}step=${this.state.currentStep.searchQuery}`
      const pageUrl = `${window.location.origin}${window.location.pathname}${search}`

      // window.dataLayer.push({
      //   event: 'virtualPageview',
      //   pageTitle,
      //   pageUrl,
      //   ga4id: business?.gaTrackingId || '',
      //   fb_pixel: business?.fcbPixelId || '',
      // })

      window.dataLayer.push({
        event: 'virtualPageview',
        pageTitle,
        pageUrl
      })
      window.dataLayer.push({
        event: 'dataLayerData',
        ga4id: business?.gaTrackingId || '',
        fb_pixel: business?.fcbPixelId || ''
      })

      // window.gtag?.('event', 'virtualPageview', {
      //   page_title: pageTitle,
      //   pageUrl,
      // })

      // window.history.pushState({ step: this.state.currentStep.searchQuery }, pageTitle, pageUrl.replace(/step=.*/, `step=${this.state.currentStep.searchQuery}`));
      if (this.state.currentStep.id === 6) { // completed step
        const res = await this.props.loadAlreadyExistingClientForBooking({
          firstName:this.state.client.firstName,
          lastName:this.state.client.lastName,
          email:this.state.client.email,
          mobileNumber:this.state.client.mobileNumber,
          businessId: business?.id
        })

      const businessLocation = this.state.stepsData.location?.label;
      const businessServices = this.state.stepsData.service?.name;
      const businessServicesPrice = this.state.stepsData.service?.price;
      const businessNewClient = res?.payload?.isNewClient
      const totalValue = res?.payload?.totalValue
      const categories = prepareCategories(serviceCategories?.byId || {});
      const categoryName = categories?.find(category => category.id === this.state.stepsData.service?.serviceCategoryId)?.name || '';

      const options = {
        event: 'Booking',
        ga4id: business?.gaTrackingId || '',
        fb_pixel: business?.fcbPixelId || '',
        client_email: this.state.client.email,
        order_value: businessServicesPrice / 100,
        currency: business?.currency,
        category: categoryName,
        service: businessServices,
        location: businessLocation,
        newclient: businessNewClient,
        ga4conid: business?.gaConversionId || '',
        ga4conlbl: business?.gaConversionLabel || '',
        LTV: totalValue,
      }
        window.dataLayer.push(options)
      }
    }
  }

  handleStartLoading = () => {
    this.setState({ isLoading: true });
  }

  getClientIp = async () => {
    try {
      const { data } = await axios.get('https://ipapi.co/json/')
      .catch(err => {
        console.log('getClientIp_catch_ERROR', err.message || err)
        return axios.get('https://geolocation-db.com/json/')
        .then(res => res)
        .catch(err => {
          console.log('getClientIp_catch_2_ERROR', err.message || err)
          return {}
        })
      })
      const browserVersion = navigator.userAgent;
      if ((data?.ip && data?.ip !== "Not found") || (data?.IPv4 && data?.IPv4 !== "Not found")) {
        const clientIpAddress = data?.ip || data?.IPv4
        const lat = data?.latitude;
        const lng = data?.longitude;
        const clientTimezone = data?.timezone;
        const countryCode = data?.country_code;
        const country = data?.country_name;
        const city = data?.city;
        this.setState(({ client }) => ({
          client: {
            ...client,
            ...(clientIpAddress ? { clientIpAddress } : {}),
            ...(lat ? { lat } : {}),
            ...(lng ? { lng } : {}),
            ...(clientTimezone ? { clientTimezone } : {}),
            ...(browserVersion ? { browserVersion } : {}),
            ...(countryCode ? { countryCode } : {}),
            ...(country ? { country } : {}),
            ...(city ? { city } : {})
          },
        }));
      } else if (browserVersion) {
        this.setState(({ client }) => ({
          client: {
            ...client,
            ...(browserVersion ? { browserVersion } : {}),
          },
        }));
      }
    } catch (error) {
      console.log('getClientIp_try_catch_ERROR', error)
    }
  }

  handleStopLoading = () => {
    this.setState({ isLoading: false });
  }

  handleExpiredSession() {
    const { tempUnavailableId } = this.state;
    if (tempUnavailableId) {
      BookingApi.deleteTempUnavailableTime({tempUnavailableId});
      this.setTempUnavailableId(null);
    }
    this.setState({ isSessionExpired: true });
  }

  handlePrevStep() {
    const { currentStep, stepsData } = this.state;
    const prevStep = this.bookingFlow.getPrevVisibleStep(
      currentStep,
      stepsData,
    );

    this.setState({ currentStep: prevStep });
  }

  async handleNextStep(newStepData, areValuesChanged) {
    this.handleStartLoading();

    const { currentStep } = this.state;

    let stepsData = { ...this.state.stepsData };
    stepsData = this.bookingFlow.updateStepsData(stepsData, newStepData);
    if (areValuesChanged) {
      stepsData = this.bookingFlow.resetDataForNextSteps(
        currentStep,
        stepsData,
      );
      stepsData = await this.bookingFlow.prepareNextStep(
        currentStep,
        stepsData,
        this.props,
      );
    }

    const nextStep = this.bookingFlow.getNextVisibleStep(currentStep, stepsData);

    this.setState({
      stepsData,
      currentStep: nextStep,
    }, this.handleStopLoading);
  }

  async handleCompliteBooking({overrideUnmatchedField = false, createNewClientIfFirstNameNotMatches = false} = {}) {
    this.handleStartLoading();

    const {
      appointment,
      client,
      currentStep,
      bookingOptions,
      stepsData,
      tempUnavailableId
    } = this.state;
    const { business } = this.props;

    try {
      const stepsResultData = this.bookingFlow.getStepsResultData(stepsData);
      const preparedData = prepareData(
        appointment,
        client,
        business.id,
        stepsResultData,
        overrideUnmatchedField,
        createNewClientIfFirstNameNotMatches,
        tempUnavailableId
      );
      await BookingApi.createAppointment(preparedData);
      if (tempUnavailableId) {
        await BookingApi.deleteTempUnavailableTime({tempUnavailableId});
        this.setTempUnavailableId(null);
      }

      const {
        allowSubscription,
      } = await MailchimpApi.checkSubscription({
        businessId: business.id,
        email: client.email,
        firstName: client.firstName,
        lastName: client.lastName,
      });

      if (bookingOptions.rememberMe) {
        keepClientDataInBrowser(client);
      } else {
        clearClientDataFromBrowser();
      }

      const nextStep = this.bookingFlow.getNextVisibleStep(
        currentStep,
        stepsData,
      );
      this.setState({
        allowSubscription,
        currentStep: nextStep,
      }, this.handleStopLoading);
    } catch (error) {
      if (['OverlapAppointmentsByPaddingsError', 'AppointmentFromPastError'].includes(error.name)) {
        const prevStep = this.bookingFlow.getPrevVisibleStep(
          currentStep,
          stepsData,
        );
        const newStepsData = this.bookingFlow.resetStepData(
          prevStep,
          stepsData,
        );

        this.setState({
          currentStep: prevStep,
          stepsData: newStepsData,
        }, () => {
          this.props.handleDisplayFlashMessage(error.message, 'error');
          this.handleStopLoading();
          if (tempUnavailableId) {
            BookingApi.deleteTempUnavailableTime({tempUnavailableId});
            this.setTempUnavailableId(null);
          }
        });
        return;
      }
      if (error.name === 'UnmatchedClientFieldError') {
        this.handleStopLoading();
        // Pass error to be handled in ClientStep
        throw error
      }
      // if (error.name) {
      //   this.props.handleDisplayFlashMessage(error.message, 'error');
      //   this.handleStopLoading();
      // }
      console.log(error);
      this.handleStopLoading();
    }
  }

  handleChangeAppointment(name, value) {
    this.setState(({ appointment }) => ({
      appointment: {
        ...appointment,
        [name]: value,
      },
    }));
  }

  handleChangeClient(name, value) {
    this.setState(({ client }) => ({
      client: {
        ...client,
        [name]: value,
      },
    }));
  }

  handleChangeBookingOptions(name) {
    const { bookingOptions } = this.state;
    const option = bookingOptions[name];

    this.setState({
      bookingOptions: {
        ...bookingOptions,
        [name]: !option,
      },
    });
  }

  async handleSubscribeClient() {
    this.handleStartLoading();
    try {
      const { client } = this.state;
      const { business } = this.props;

      await CustomersApi.subscribe({
        businessId: business.id,
        email: client.email,
        firstName: client.firstName,
        lastName: client.lastName,
      });
    } catch (error) {
      console.log('handleSubscribeClient_Error', error?.message)
    }
    this.setState({
      hasClientJustSubscribed: true,
    }, this.handleStopLoading);
  }

  setTempUnavailableId = tempUnavailableId => {
    this.setState({tempUnavailableId});
  }

  setUnloadListener = unloadListener => {
    this.setState({unloadListener});
  }

  render() {
    const {
      currentStep,
      isBookingInitialized,
      isSessionExpired,
      areThereBookableServices,
      isLoading,
      tempUnavailableId
    } = this.state;
    const { classes, business } = this.props;
    console.log('tempUnavailableId', tempUnavailableId)

    const StepComponent = currentStep ? stepComponents[currentStep.id] : <div />;

    return (
      <div style={{display: 'flex', justifyContent: 'center', fontFamily: 'Roboto', height: '100%'}}>
        <CircularProgressWithBackdrop loading={isLoading} />
        <Grid className={classes.root}>
          <header>
            {/* <Grid className={classes.title}> */}
              <Box sx={{
                  display:'flex',
                  alignItems:'end',
                  justifyContent:'end',
                  width:'100%',                
                }}
                  >

                  <Box className={classes.avatarPosition}>
                    <Box sx={{
                      display:'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width:'100%'
                    }}>
                      {business?.logo ? 
                        <CardContent
                          component="img"
                          width="400"
                          height='200'
                          src={composeGCSUrl(business.logo)}
                          sx={{
                            width:'100%',
                            objectFit:'contain',
                            objectPosition: 'top',
                            height: 'auto',
                          }}
                      /> :
                        <Typography sx={{padding:'1.8rem'}} variant="h5">{business.name && business.name.length > 25 ? `${business.name.slice(0,25)} ...` : business.name}</Typography>
                    }
                    </Box>
                  </Box>
              </Box>
            {/* </Grid> */}
          </header>

          <div className={classes.contentPosition}>
            {isBookingInitialized && !isSessionExpired && (
              <StepComponent
                {...this.state}
                {...this.props}
                bookingFlow={this.bookingFlow}
                handleStartLoading={this.handleStartLoading}
                handleStopLoading={this.handleStopLoading}
                handlePrevStep={this.handlePrevStep}
                handleNextStep={this.handleNextStep}
                handleCompliteBooking={this.handleCompliteBooking}
                handleChangeAppointment={this.handleChangeAppointment}
                handleChangeClient={this.handleChangeClient}
                handleChangeBookingOptions={this.handleChangeBookingOptions}
                handleSubscribeClient={this.handleSubscribeClient}
                setTempUnavailableId={this.setTempUnavailableId}
                setUnloadListener={this.setUnloadListener}
              />
            )}
          </div>

          {isSessionExpired && (
            <Grid container className={classes.expiredSessionBox}>
              <Typography variant="h5">Session expired</Typography>
              <Typography variant="subtitle1">Please reload the page</Typography>
            </Grid>
          )}

          {!areThereBookableServices && (
            <Grid container className={classes.noBookableStaffBox}>
              <Typography variant="subtitle1">
                Sorry, there are no boobkable services at this time
              </Typography>
            </Grid>
          )}

          <IdleTimer
            ref={ref => { this.idleTimer = ref }}
            onIdle={this.handleExpiredSession}
            timeout={idleMinutes * 60 * 1000}
          />
        </Grid>
      </div>
    );
  }
};

const mapStateToProps = ({
  clientStatus,
  business,
  locations,
  serviceCategories,
  staff,
}) => ({
  clientStatus,
  business,
  locations,
  serviceCategories,
  staff,
});

const mapDispatchToProps = dispatch => ({
  loadBusinessBySlugForBooking: bindActionCreators(
    loadBusinessBySlugForBooking,
    dispatch,
  ),
  loadAlreadyExistingClientForBooking: bindActionCreators(loadClientForBooking, dispatch),
  loadLocationsForBooking: bindActionCreators(loadLocationsForBooking, dispatch),
  loadCategoriesWithServicesForBooking: bindActionCreators(
    loadCategoriesWithServicesForBooking, dispatch,
  ),
  loadStaffForBooking: bindActionCreators(loadStaffForBooking, dispatch),
  createClientForBooking: bindActionCreators(createClientForBooking, dispatch),
  verifyClientCreditCard: bindActionCreators(verifyClientCreditCard, dispatch),
  createClientCreditCard: bindActionCreators(createClientCreditCard, dispatch),
});

export default compose(
  withFlashMessage,
  connect(mapStateToProps, mapDispatchToProps),
)(withStyles(BookPage, styles));
